const GOOGLE_GEMINI_API_KEY = process.env.REACT_APP_GOOGLE_GEMINI_API_KEY || process.env.REACT_APP_GOOGLE_VISION_API_KEY || "AIzaSyAF54YAMi6ifCcfW2fknswJs6cE7VypzM0";

const fileUrlToBase64 = async (url) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("Failed to fetch or convert file:", error);
    return null;
  }
};

export const processAIQuery = async (query, context) => {
  const { cases, clients, documents, dbOps, currentUserProfile } = context;

  const casesSummary = cases?.map(c => `ID: ${c.id}, Title: ${c.title}`).join('\n') || '';
  
  const intentPrompt = `
    You are an intelligent router for an Israeli law firm CRM.
    The user is asking: "${query}"
    
    Available cases in the system:
    ${casesSummary}

    Analyze the user's intent. Do they want to DRAFT or GENERATE a new document based on a specific case?
    If yes, identify the exact Case ID from the available cases list (matching by name or ID).
    
    Return a strictly valid JSON object without markdown wrappers or code blocks:
    {
      "action": "draft_document" | "general_chat",
      "caseId": "The identified case ID or null if not found"
    }
  `;

  try {
    const intentResponse = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${GOOGLE_GEMINI_API_KEY}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contents: [{ parts: [{ text: intentPrompt }] }],
        generationConfig: { responseMimeType: "application/json" }
      })
    });
    
    const intentData = await intentResponse.json();
    let intentResult = { action: 'general_chat', caseId: null };
    
    try {
      if (intentData.candidates?.[0]?.content?.parts?.[0]?.text) {
        intentResult = JSON.parse(intentData.candidates[0].content.parts[0].text);
      }
    } catch (e) {
      console.error("Failed to parse intent JSON", e);
    }

    if (intentResult.action === 'draft_document' && intentResult.caseId) {
      const targetCase = cases.find(c => c.id === intentResult.caseId);
      const caseDocs = documents.filter(d => d.caseId === intentResult.caseId);
      
      if (caseDocs.length === 0) {
        return { text: `זיהיתי שאתה רוצה לנסח מסמך עבור תיק "${targetCase.title}", אבל לא מצאתי בו אף מסמך מצורף. כדי שאוכל להתבסס על מסמכי התיק, אנא העלה אליו מסמכים קודם.` };
      }

      const documentParts = [];
      const docsToProcess = caseDocs.slice(0, 10);
      
      for (const doc of docsToProcess) {
        if (doc.textContent) {
          documentParts.push({ text: `--- Document Name: ${doc.name} ---\n${doc.textContent}\n--- End Document ---` });
        } else if (doc.fileUrl) {
          const base64 = await fileUrlToBase64(doc.fileUrl);
          if (base64) {
             let mimeType = 'application/pdf';
             if (doc.name.toLowerCase().endsWith('.png')) mimeType = 'image/png';
             if (doc.name.toLowerCase().endsWith('.jpg') || doc.name.toLowerCase().endsWith('.jpeg')) mimeType = 'image/jpeg';
             
             documentParts.push({ text: `--- Attached file: ${doc.name} ---` });
             documentParts.push({ inlineData: { mimeType, data: base64 } });
          }
        }
      }

      const draftingPrompt = `
        You are an elite legal AI assistant in an Israeli law firm.
        The user wants you to draft a legal document for case "${targetCase.title}" (ID: ${targetCase.id}).
        User request: "${query}"
        
        Lawyer name: ${currentUserProfile?.name || 'עורך דין'}
        
        Below are the contents and files of the documents currently in this case. Analyze them carefully and draft the requested document based on the facts within them.
        Draft it in professional legal Hebrew. Return ONLY the drafted text. Do not wrap it in markdown block quotes.
      `;

      const draftResponse = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${GOOGLE_GEMINI_API_KEY}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{ parts: [{ text: draftingPrompt }, ...documentParts] }]
        })
      });

      const draftData = await draftResponse.json();
      const draftedText = draftData.candidates?.[0]?.content?.parts?.[0]?.text;

      if (!draftedText) {
        return { text: "מצטער, חלה שגיאה ביצירת המסמך על ידי ה-AI." };
      }

      if (dbOps && typeof dbOps.handleSave === 'function') {
        const newDoc = {
          name: `מסמך שנוצר על ידי AI - ${new Date().toLocaleDateString('he-IL')}.txt`,
          date: new Date().toISOString().split('T')[0],
          caseId: targetCase.id,
          securityLevel: 'רגיל',
          folder: 'מסמכים מיוצרים - AI',
          textContent: draftedText
        };
        dbOps.handleSave('documents', newDoc);
      }

      return { text: `הכנתי עבורך את המסמך המבוקש בהתבסס על המסמכים המצורפים לתיק "${targetCase.title}".\nהמסמך נשמר תחת התיקייה "מסמכים מיוצרים - AI" בתיק זה.\n\nהנה התוכן שנוצר:\n\n---\n${draftedText}` };
    }

    const contextPrompt = `
      You are an elite legal AI assistant in an Israeli law firm CRM.
      Answer the user's question.
      You have access to the following metadata (No full files, just metadata):
      Cases: ${casesSummary}
      Clients: ${clients?.map(c => `Name: ${c.name}, ID: ${c.idNumber}`).join(' | ')}
      
      User query: "${query}"
      Answer in Hebrew. Be helpful and professional.
    `;

    const generalResponse = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${GOOGLE_GEMINI_API_KEY}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{ parts: [{ text: contextPrompt }] }]
        })
    });
    const generalData = await generalResponse.json();
    const generalText = generalData.candidates?.[0]?.content?.parts?.[0]?.text;

    return { text: generalText || "לא הבנתי את הבקשה." };

  } catch (err) {
    console.error("AI Agent error:", err);
    return { text: "מצטער, הייתה שגיאה בתקשורת מול שרתי ה-AI. ייתכן שיש בעיה בחיבור או בהגדרות הרשת." };
  }
};
